/**
 * Admin Settings API Service
 * 
 * Provides functions for managing trading system settings:
 * - Get settings schema with current values
 * - Bulk update settings
 * - Reset settings to defaults
 * - Get audit log
 */

import { apiClient } from "./apiClient";
import { logger } from "./logger";

export interface SettingOption {
  value: string | number;
  label: string;
}

export interface SettingDefinition {
  key: string;
  label: string;
  type: "bool" | "int" | "float" | "string" | "select" | "json";
  default_value: any;
  current_value: any;
  constraints?: {
    min?: number;
    max?: number;
    step?: number;
    options?: (string | number | SettingOption)[];
  };
  description?: string;
  is_dangerous?: boolean;
  warning_text?: string;
  is_readonly?: boolean;
}

export interface SettingsSchema {
  [section: string]: {
    [subsection: string]: SettingDefinition[];
  };
}

export interface SettingUpdate {
  key: string;
  value: any;
}

export interface BulkUpdateRequest {
  updates: SettingUpdate[];
  scope?: string;
}

export interface ResetRequest {
  scope: "all" | "section";
  section?: string;
}

export interface AuditLogEntry {
  id: string;
  admin_user_id: string;
  setting_key: string;
  old_value: any;
  new_value: any;
  scope: string;
  section: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  count: number;
  limit: number;
  offset: number;
}

export interface ExportResponse {
  exported_at: string;
  settings_version: string;
  settings: Record<string, any>;
  defaults_snapshot?: Record<string, any>;
  filter?: { tab?: string };
}

export interface ImportPreviewDiff {
  key: string;
  label: string;
  section: string;
  old_value: any;
  new_value: any;
  error?: string;
  skipped?: boolean;
  action?: string;
}

export interface ImportPreviewResponse {
  status: "preview" | "success" | "error";
  preview: ImportPreviewDiff[];
  unknown_keys: string[];
  validation_errors?: Array<{ key: string; label: string; error: string }>;
  changes_count: number;
  skipped_count?: number;
  imported_count?: number;
  message?: string;
  errors?: string[];
}

export interface ImportRequest {
  settings: Record<string, any>;
  mode?: "merge" | "replace";
  preview_only?: boolean;
}

/**
 * Get settings schema with current values
 */
export async function getSettingsSchema(
  section?: string,
  includeCurrentValues: boolean = true
): Promise<SettingsSchema> {
  try {
    const params = new URLSearchParams();
    if (section) {
      params.append("section", section);
    }
    params.append("include_current_values", includeCurrentValues.toString());

    const response = await apiClient.get<SettingsSchema>(
      `/admin/settings/schema?${params.toString()}`
    );
    
    // Debug: Log raw response
    console.log("Raw API response:", {
      data: response.data,
      type: Array.isArray(response.data) ? "array" : typeof response.data,
      isArray: Array.isArray(response.data),
      keys: Array.isArray(response.data) ? `Array length: ${response.data.length}` : Object.keys(response.data || {}),
    });
    
    // Ensure response is an object, not an array
    if (Array.isArray(response.data)) {
      logger.warn("API returned array instead of object! Converting...", { dataLength: Array.isArray(response.data) ? response.data.length : "unknown" });
      // Convert array to object if needed (shouldn't happen, but defensive)
      const converted: SettingsSchema = {};
      response.data.forEach((item: any, index: number) => {
        if (typeof item === "object" && item !== null) {
          // Try to extract section name from item
          const sectionName = item.section || item.name || `Section${index}`;
          converted[sectionName] = item.subsections || item.data || {};
        }
      });
      return converted;
    }
    
    return response.data;
  } catch (error) {
    logger.error("Error fetching settings schema:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Bulk update settings
 */
export async function updateSettings(
  request: BulkUpdateRequest
): Promise<{ status: string; message: string; updated_count: number }> {
  try {
    const response = await apiClient.patch(
      `/admin/settings/update`,
      request
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Reset settings to defaults
 */
export async function resetSettings(
  request: ResetRequest
): Promise<{ status: string; message: string }> {
  try {
    const response = await apiClient.post(
      `/admin/settings/reset`,
      request
    );
    return response.data;
  } catch (error) {
    logger.error("Error resetting settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Fetch audit log
 */
export async function getAuditLog(
  limit: number = 100,
  offset: number = 0,
  settingKey?: string,
  adminUserId?: string,
  section?: string
): Promise<AuditLogResponse> {
  try {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());
    if (settingKey) {
      params.append("setting_key", settingKey);
    }
    if (adminUserId) {
      params.append("admin_user_id", adminUserId);
    }
    if (section) {
      params.append("section", section);
    }

    const response = await apiClient.get<AuditLogResponse>(
      `/admin/settings/audit?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    logger.error("Error fetching audit log:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Export all settings as JSON
 */
export async function exportSettings(
  tab?: string,
  includeDefaults: boolean = false
): Promise<ExportResponse> {
  try {
    const params = new URLSearchParams();
    if (tab) {
      params.append("tab", tab);
    }
    params.append("include_defaults", includeDefaults.toString());

    const response = await apiClient.get<ExportResponse>(
      `/admin/settings/export?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    logger.error("Error exporting settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Preview import changes without applying
 */
export async function previewImport(
  request: ImportRequest
): Promise<ImportPreviewResponse> {
  try {
    const response = await apiClient.post<ImportPreviewResponse>(
      `/admin/settings/import/preview`,
      { ...request, preview_only: true }
    );
    return response.data;
  } catch (error) {
    logger.error("Error previewing import:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Import settings from JSON
 */
export async function importSettings(
  request: ImportRequest
): Promise<ImportPreviewResponse> {
  try {
    const response = await apiClient.post<ImportPreviewResponse>(
      `/admin/settings/import`,
      { ...request, preview_only: false }
    );
    return response.data;
  } catch (error) {
    logger.error("Error importing settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export interface TelegramNotificationSettings {
  bot_token?: string | null;
  bot_link?: string | null;
}

export interface TelegramNotificationSettingsResponse {
  status: string;
  message?: string;
  updated_keys?: string[];
}

/**
 * Get telegram notification bot settings
 */
export async function getTelegramNotificationSettings(): Promise<TelegramNotificationSettings> {
  try {
    const response = await apiClient.get<TelegramNotificationSettings>(
      "/admin/settings/telegram-notifications"
    );
    return response.data;
  } catch (error) {
    logger.error("Error getting telegram notification settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Update telegram notification bot settings
 */
export async function updateTelegramNotificationSettings(
  settings: TelegramNotificationSettings
): Promise<TelegramNotificationSettingsResponse> {
  try {
    const response = await apiClient.patch<TelegramNotificationSettingsResponse>(
      "/admin/settings/telegram-notifications",
      settings
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating telegram notification settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export interface SmtpSettings {
  host?: string | null;
  port?: number | null;
  username?: string | null;
  from_email?: string | null;
  use_tls?: boolean | null;
  password_set?: boolean;
  password_masked?: string | null;
}

export interface SmtpSettingsUpdate {
  host?: string | null;
  port?: number | null;
  username?: string | null;
  password?: string | null;
  from_email?: string | null;
  use_tls?: boolean | null;
}

export interface SmtpTestResponse {
  status: string;
  message: string;
}

/**
 * Get current SMTP (email) settings
 */
export async function getSmtpSettings(): Promise<SmtpSettings> {
  try {
    const response = await apiClient.get<SmtpSettings>("/admin/settings/smtp");
    return response.data;
  } catch (error) {
    logger.error("Error getting SMTP settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Update SMTP (email) settings
 */
export async function updateSmtpSettings(
  settings: SmtpSettingsUpdate
): Promise<{ status: string; message: string }> {
  try {
    const response = await apiClient.patch<{ status: string; message: string }>(
      "/admin/settings/smtp",
      settings
    );
    return response.data;
  } catch (error) {
    logger.error("Error updating SMTP settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Send a test email through the configured SMTP
 */
export async function testSmtpSettings(
  toEmail: string
): Promise<SmtpTestResponse> {
  try {
    const response = await apiClient.post<SmtpTestResponse>(
      "/admin/settings/smtp/test",
      { to_email: toEmail }
    );
    return response.data;
  } catch (error) {
    logger.error("Error sending SMTP test email:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export type ReportPeriod = "daily" | "weekly" | "monthly";
export type ReportScope = "per_user" | "global";

export interface ScheduledReportConfig {
  period: ReportPeriod;
  enabled: boolean;
  hour: number;
  utc_offset: number;
  scope: ReportScope;
  channels: string[];
  day_of_week: number;
  day_of_month: number;
  last_sent: string | null;
}

export interface ScheduledReportsResponse {
  periods: ScheduledReportConfig[];
}

export interface ScheduledReportSettingsUpdate {
  period: ReportPeriod;
  enabled?: boolean;
  hour?: number;
  utc_offset?: number;
  scope?: ReportScope;
  channels?: string[];
  day_of_week?: number;
  day_of_month?: number;
}

export interface ScheduledReportSendNowResponse {
  status: string;
  message: string;
  summary?: {
    total: number;
    sent: number;
    skipped: number;
    failed: number;
  };
}

/**
 * Get scheduled report settings for all periods
 */
export async function getScheduledReportSettings(): Promise<ScheduledReportsResponse> {
  try {
    const response = await apiClient.get<ScheduledReportsResponse>(
      "/admin/settings/scheduled-reports"
    );
    return response.data;
  } catch (error) {
    logger.error("Error getting scheduled report settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Update scheduled report settings for a period
 */
export async function updateScheduledReportSettings(
  settings: ScheduledReportSettingsUpdate
): Promise<{ status: string; message: string; config: ScheduledReportConfig }> {
  try {
    const response = await apiClient.patch<{
      status: string;
      message: string;
      config: ScheduledReportConfig;
    }>("/admin/settings/scheduled-reports", settings);
    return response.data;
  } catch (error) {
    logger.error("Error updating scheduled report settings:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

/**
 * Trigger a scheduled report broadcast immediately (test)
 */
export async function sendScheduledReportNow(
  period: ReportPeriod
): Promise<ScheduledReportSendNowResponse> {
  try {
    const response = await apiClient.post<ScheduledReportSendNowResponse>(
      "/admin/settings/scheduled-reports/send-now",
      { period }
    );
    return response.data;
  } catch (error) {
    logger.error("Error sending scheduled report now:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

